<template>
  <div class="app-container">
    <!-- Sidebar -->
    <aside class="app-sidebar" :class="{ 'sidebar-active': isSidebarOpen }">
      <div class="sidebar-header">
        <i class="pi pi-bolt text-yellow-500 mr-2"></i>
        <span>Navigation</span>
      </div>
      <nav class="sidebar-nav">
        <router-link 
          v-for="route in navigationRoutes" 
          :key="route.name"
          :to="{ name: route.name }"
          class="nav-item"
          :class="{ active: currentRoute === route.name }"
        >
          <i :class="route.meta?.icon || 'pi pi-circle-fill'"></i>
          <span>{{ route.meta?.label || route.name }}</span>
        </router-link>
      </nav>
    </aside>

    <!-- Main Content -->
    <div class="main-wrapper">
      <header class="app-header">
        <div class="app-title">
          <Button 
            icon="pi pi-bars" 
            text 
            @click="toggleSidebar"
            class="sidebar-toggle"
          />
          <i class="pi pi-bolt text-yellow-500 mr-2"></i>
          <h1>Energy Dashboard</h1>
        </div>
        <div class="app-navbar">
          <span class="user-name mr-2">{{ user.username }}</span>
          <Button icon="pi pi-power-off" class="p-button-rounded p-button-warning" @click="logout" />
        </div>
      </header>
      
      <main class="app-content">
        <router-view></router-view>
      </main>
      
      <footer class="app-footer">
        <p>&copy; {{ new Date().getFullYear() }} Energy Dashboard</p>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()
const user = ref({ username: '', email: '' })
const isSidebarOpen = ref(false)

// Get navigation routes from router configuration
const navigationRoutes = computed(() => {
  return router.options.routes
    .find(r => r.path === '/')?.children
    ?.filter(route => !route.meta?.hidden) || []
})

const currentRoute = computed(() => route.name)

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

// ...existing code for onMounted, fetchUserData, and logout...
</script>

<style lang="scss" scoped>
.app-container {
  display: flex;
  min-height: 100vh;
}

.app-sidebar {
  width: 250px;
  background-color: var(--surface-0);
  border-right: 1px solid var(--surface-200);
  transition: transform 0.3s ease;
  
  .sidebar-header {
    padding: 1rem;
    font-size: 1.2rem;
    font-weight: 600;
    border-bottom: 1px solid var(--surface-200);
    display: flex;
    align-items: center;
  }
  
  .sidebar-nav {
    padding: 1rem;
    
    .nav-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      color: var(--text-color);
      text-decoration: none;
      border-radius: 8px;
      margin-bottom: 0.5rem;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: var(--surface-100);
      }
      
      &.active {
        background-color: var(--primary-100);
        color: var(--primary-700);
      }
      
      i {
        font-size: 1.1rem;
      }
    }
  }
}

.main-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.app-header {
  // ...existing header styles...
  .sidebar-toggle {
    display: none; // Hidden by default on desktop
  }
}

// Mobile styles
@media screen and (max-width: 768px) {
  .app-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    transform: translateX(-100%);
    
    &.sidebar-active {
      transform: translateX(0);
    }
  }
  
  .app-header {
    .sidebar-toggle {
      display: block;
      margin-right: 1rem;
    }
  }
}


  
  .app-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: var(--primary-color);
    color: var(--primary-color-text);
    padding: 1rem 2rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    .app-title {
      display: flex;
      align-items: center;
      
      h1 {
        margin: 0;
        font-size: 1.5rem;
      }
    }
    
    .app-navbar {
      display: flex;
      align-items: center;
      
      .user-name {
        font-weight: 500;
      }
    }
  }
  
  .app-content {
    flex: 1;
    padding: 2rem;
  }
  
  .app-footer {
    background-color: var(--surface-section);
    text-align: center;
    padding: 1rem;
    font-size: 0.9rem;
    color: var(--text-color-secondary);
  }
  
  @media screen and (max-width: 768px) {
    .app-header {
      padding: 1rem;
      
      .app-title h1 {
        font-size: 1.2rem;
      }
    }
    
    .app-content {
      padding: 1rem;
    }
  }</style>